/**
 * What the loading scene loads, and how it knows it is done. The game draws most of its pictures
 * the first time they are needed (figure frames in workers, ground chunks, scenery, beasts,
 * window contents, GPU uploads); here they are asked for ahead, behind the loading scene, and the
 * scene only lifts once every queue has run dry, so nothing pops in or hitches once play starts.
 *
 * Client-only: the world is read, never changed (the camera is pointed round the map through the
 * renderer's cutscene staging, the windows are opened and closed behind the scene).
 */
import { lookOf, UI_GLYPHS, type World } from '@nine/shared';
import { groundJobs } from '../render/groundWorkers';
import type { Renderer } from '../render/renderer';
import { ahead, idleFrames } from '../render/rig/sets';
import { frameJobs } from '../render/rig/workers';
import type { SpriteBank, SpriteSet } from '../render/sprites';
import { loadSave, SLOTS } from '../storage';
import { BRUSH_HAN, COLUMN_HAN } from '../ui/frontInk';
import type { PanelTab, Panels } from '../ui/panels';
import type { MapView } from '../ui/worldmap';
import { nextFrame, within, type Preloader, type Report, type StepDef } from './loader';

/** Steps of every load: the code, fonts, the world, bakes, the GPU, the screens. */
export const BOOT_STEPS = {
  code: { label: 'กำลังโหลดเกม…', weight: 4 },
  fonts: { label: 'กำลังโหลดฟอนต์…', weight: 8 },
  world: { label: 'กำลังเตรียมยุทธภพ…', weight: 3 },
  bakes: { label: 'กำลังโหลดภาพตัวละคร…', weight: 4 },
  gpu: { label: 'กำลังเตรียมการ์ดจอ…', weight: 6 },
  ui: { label: 'กำลังสร้างหน้าจอ…', weight: 5 },
} as const satisfies Record<string, StepDef>;

/** On to the login and character select: the characters shown there. */
export const FRONT_STEPS = {
  front: { label: 'กำลังเตรียมตัวละคร…', weight: 10 },
} as const satisfies Record<string, StepDef>;

/** Into the world: everything the first screens of play show. */
export const WORLD_STEPS = {
  panels: { label: 'กำลังเตรียมหน้าต่างเมนู…', weight: 6 },
  figures: { label: 'กำลังเตรียมตัวละคร…', weight: 44 },
  scenery: { label: 'กำลังสร้างแผนที่…', weight: 20 },
  settle: { label: 'กำลังเก็บรายละเอียดสุดท้าย…', weight: 6 },
  upload: { label: 'กำลังส่งภาพขึ้นการ์ดจอ…', weight: 10 },
} as const satisfies Record<string, StepDef>;

export type BootStep = keyof typeof BOOT_STEPS;
export type FrontStep = keyof typeof FRONT_STEPS;
export type WorldStep = keyof typeof WORLD_STEPS;

// ---------------------------------------------------------------- queues

/** Pictures asked for so far (figure frames, idle-time frames, ground chunks). */
const asked = (): number => frameJobs.asked + idleFrames.queued + groundJobs.asked;
/** Pictures asked for and not finished. */
const waiting = (): number => frameJobs.waiting + idleFrames.waiting() + groundJobs.waiting();

/** Waits until every picture asked for is finished, reporting the share done since the call. */
async function drain(report: Report): Promise<void> {
  const base = asked() - waiting();
  for (;;) {
    const w = waiting();
    const all = asked() - base;
    report(all > 0 ? (all - w) / all : 1);
    if (!w) return;
    await nextFrame();
  }
}

// ---------------------------------------------------------------- fonts

/** Every face the UI and the world's lettering use, with text that pulls in their Thai and Latin parts. */
const FACES = [
  '300 16px Mitr',
  '400 16px Mitr',
  '500 16px Mitr',
  '600 16px Mitr',
  '700 16px Srisakdi',
  '500 16px "Pixelify Sans"',
  '700 16px "Pixelify Sans"',
  // The ink-wash front screens and loading scene (index.html's inkFontsCss).
  '400 16px "Noto Serif Thai"',
  '500 16px "Noto Serif Thai"',
  '600 16px "Noto Serif Thai"',
  '700 16px "Noto Serif Thai"',
  '700 16px Charmonman',
];
const SAMPLE =
  'เก้ากระบี่ออนไลน์ ยุทธภพ กำลังภายใน ฝึกปรือ ABCDEFGHIJKLMNOPQRSTUVWXYZ abcdefghijklmnopqrstuvwxyz 0123456789 ·…“”×%+−';
/** A font server that never answers: the fallback fonts are used rather than waiting forever. */
const FONT_STALL_MS = 30_000;

function sheetReady(link: HTMLLinkElement): Promise<void> {
  const ready = () => {
    link.media = 'all';
  };
  if (link.dataset.failed) return Promise.reject(new Error('font stylesheet failed'));
  if (link.sheet) {
    ready();
    return Promise.resolve();
  }
  return new Promise((ok, fail) => {
    link.addEventListener(
      'load',
      () => {
        ready();
        ok();
      },
      { once: true },
    );
    link.addEventListener('error', () => fail(new Error('font stylesheet failed')), { once: true });
  });
}

/** A stylesheet of Google faces, appended to the page. */
function fontSheet(query: string): HTMLLinkElement {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?${query}&display=swap`;
  document.head.append(link);
  return link;
}

/**
 * Loads every font face before anything is shown in it: the UI's (index.html links them) and the
 * Han display face, subset to exactly the glyphs the UI uses. The front screens' brush and column
 * glyphs (ui/frontInk.ts, all in UI_GLYPHS) come subset to just themselves. Nothing swaps fonts
 * after the load.
 */
export async function loadFonts(report: Report): Promise<void> {
  const han = fontSheet(`family=Noto+Serif+TC:wght@900&text=${encodeURIComponent(UI_GLYPHS)}`);
  const brush = fontSheet(
    `family=Ma+Shan+Zheng&family=Noto+Serif+TC:wght@500&text=${encodeURIComponent(BRUSH_HAN + COLUMN_HAN)}`,
  );
  const sheets = ['fontsCss', 'inkFontsCss'].flatMap((id) => {
    const link = document.getElementById(id);
    return link instanceof HTMLLinkElement ? [link] : [];
  });
  const jobs: [string, string][] = [
    ...FACES.map((f): [string, string] => [f, SAMPLE]),
    ['900 16px "Noto Serif TC"', UI_GLYPHS],
    ['500 16px "Noto Serif TC"', COLUMN_HAN],
    ['400 16px "Ma Shan Zheng"', BRUSH_HAN],
  ];
  let done = 0;
  const missing: string[] = [];
  const work = async () => {
    // A stylesheet that fails leaves its faces missing (reported below); the rest still load.
    await Promise.allSettled([...sheets, han, brush].map(sheetReady));
    await Promise.all(
      jobs.map(async ([face, text]) => {
        const got = await document.fonts.load(face, text).catch(() => []);
        if (!got.length) missing.push(face);
        report(++done / jobs.length);
      }),
    );
    await document.fonts.ready;
  };
  await within(work(), FONT_STALL_MS, 'fonts');
  if (missing.length) throw new Error(`fonts not found: ${missing.join(', ')}`);
}

// ---------------------------------------------------------------- figures

/** A figure and its other camera angles. */
const allViews = (set: SpriteSet): SpriteSet[] => [
  set,
  ...Object.values(set.views ?? {}).filter((v): v is SpriteSet => !!v),
];

/** Asks for a figure's standing and walking (or wing-beat) frames in every view, and blinks. */
function askEveryday(set: SpriteSet): void {
  ahead(() => {
    for (const s of allViews(set)) {
      for (let i = 0; i < s.idle.length; i++) void s.idle[i];
      for (let i = 0; i < s.walk.length; i++) void s.walk[i];
      for (let i = 0; i < (s.fly?.length ?? 0); i++) void s.fly![i];
    }
  });
}

/**
 * The blinking frames of a figure, in every view. Left to after the lift: until one is drawn the
 * eyes simply stay open (the renderer shows the standing frame in its place), nothing pops in.
 */
function askBlinks(set: SpriteSet): void {
  ahead(() => {
    for (const s of allViews(set)) if (s.extra) for (let i = 0; i < s.idle.length; i++) s.extra('idle', i, true);
  });
}

/** The characters on the character select screen, turning through every direction. */
export async function warmFront(sprites: SpriteBank, report: Report): Promise<void> {
  for (let slot = 0; slot < SLOTS; slot++) {
    const pr = loadSave(slot);
    const set = pr && sprites.of(lookOf(pr));
    if (set) askEveryday(set);
  }
  await drain(report);
}

export interface WorldHooks {
  world: World;
  sprites: SpriteBank;
  renderer: Renderer;
  panels: Panels;
  mapView: MapView;
  /** Whether the game's frame loop draws the world (off while only the queues are being filled). */
  drawWorld(on: boolean): void;
}

/**
 * Whether the player's every fighting and skill motion is drawn in all five views before play
 * (`true`), or only in the view they are first seen in, the rest following ahead of need in the
 * background as the renderer asks (`false`): about 3,600 frames, the bulk of the load.
 */
export const PLAYER_MOTIONS_EVERY_VIEW = false;
/**
 * With `PLAYER_MOTIONS_EVERY_VIEW` off, the rest of the player's motions (every view) and the figures
 * far from the player are drawn in the background once play has begun (user, 2026-10-10: the load
 * took far too long), after this many ms.
 */
const AFTER_LIFT_MS = 2500;
/** Figures farther than this from the player (tiles) are not on the first screens: drawn after the lift. */
const FIRST_SCREEN = 16;
/** Foes within this many tiles can be fighting the player in the first moments of play. */
const FOE_NEAR = 12;
/** Figures within this many tiles are on screen in the first moments of play: their blinks too. */
const BLINK_NEAR = 22;
/** Points round the player the camera is pointed at to build what lies there (tiles). */
const SURVEY = 13;
/**
 * Time given to GPU uploads in each frame of the upload step: the loading scene still animates (at
 * a lower rate) while the uploads go about twice as fast as at 10 ms.
 */
const UPLOAD_MS = 24;
/** Frames in a row with nothing new asked for before the world counts as settled. */
const CALM_FRAMES = 10;
/** The upload step ends this long after the last drawn frame is uploaded, whatever is still being drawn (ms). */
const UPLOAD_GRACE_MS = 600;

/**
 * The player's figure in full (every view: standing, walking, running, the stance, the single poses,
 * putting the weapon away and drawing it, and the fighting and skill motions, see
 * `PLAYER_MOTIONS_EVERY_VIEW`), and everyone in the zone standing and walking in every view, with
 * the strikes of foes close by.
 */
async function warmFigures(
  h: WorldHooks,
  sets: Set<SpriteSet>,
  later: (() => void)[],
  report: Report,
): Promise<void> {
  const { world: w, sprites, renderer } = h;
  const p = w.player;
  const mine = sprites.of(p);
  if (mine) {
    sets.add(mine);
    mine.focus?.();
    askEveryday(mine);
    later.push(() => askBlinks(mine));
    if (PLAYER_MOTIONS_EVERY_VIEW) renderer.warmAhead(mine, true);
    else later.push(() => renderer.warmAhead(mine, true));
    const other = p.outfit.includes('sheathed')
      ? p.outfit.filter((o) => o !== 'sheathed')
      : [...p.outfit, 'sheathed'];
    const alt = sprites.of({ sprite: p.sprite, outfit: other });
    if (alt) {
      sets.add(alt);
      alt.focus?.(false);
      askEveryday(alt);
    }
  }
  // Everyone in the zone (they wander into view), a few at a time: a beast's first sight builds its
  // pictures here on the main thread. Looks are shared, so this is a few dozen figures at most.
  const farSets = new Set<SpriteSet>();
  let n = 0;
  for (const e of w.entities.values()) {
    if (e === p || e.dead) continue;
    const set = sprites.of(e);
    if (!set || sets.has(set)) continue;
    const d = Math.hypot(e.x - p.x, e.y - p.y);
    if (d > FIRST_SCREEN) {
      if (farSets.has(set)) continue;
      // Far off (not on the first screens): drawn in the background after the lift (and not
      // handed to the upload step, whose pass over the cycles would draw them now).
      farSets.add(set);
      later.push(() => {
        askEveryday(set);
        renderer.prepareAhead(set);
      });
      continue;
    }
    sets.add(set);
    askEveryday(set);
    if (d <= BLINK_NEAR) later.push(() => askBlinks(set));
    // A foe close enough to fight at once: its strikes and hits too (the renderer asks for them
    // only once a fight starts, too late to draw in time).
    if (w.isHostile(p, e) && d <= FOE_NEAR) later.push(() => renderer.warmAhead(set, false));
    if (++n % 4 === 0) await nextFrame();
  }
  await drain(report);
}

/**
 * Points the camera round the player and draws the world there behind the scene: the ground,
 * scenery, critters and beasts a few steps away are built now, not as the player walks up to them.
 */
async function survey(h: WorldHooks, report: Report): Promise<void> {
  const { world: w, renderer } = h;
  const p = w.player;
  const at: [number, number][] = [[0, 0]];
  for (const [dx, dy] of [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
    [1, 1],
    [-1, -1],
    [1, -1],
    [-1, 1],
  ] as const)
    at.push([dx * SURVEY, dy * SURVEY]);
  const look = (x: number, y: number) => {
    const held = renderer.stage;
    renderer.stage = { focus: { x, y }, puppets: held?.puppets ?? new Map(), draw: held?.draw };
    try {
      renderer.render(0);
    } finally {
      renderer.stage = held;
    }
  };
  for (let i = 0; i < at.length; i++) {
    const [dx, dy] = at[i]!;
    look(Math.max(1, Math.min(w.map.w - 2, p.x + dx)), Math.max(1, Math.min(w.map.h - 2, p.y + dy)));
    report(((i + 1) / at.length) * 0.4);
    await nextFrame();
  }
  // Back on the player at once (the camera would otherwise glide back from the last point).
  look(p.x, p.y);
  await drain((f) => report(0.4 + 0.6 * f));
}

/** Opens and closes each window of the main menu (and the map) behind the scene, so each is built. */
async function warmPanels(h: WorldHooks, report: Report): Promise<void> {
  const tabs: PanelTab[] = [
    'char',
    'bag',
    'arts',
    'neigong',
    'school',
    'skin',
    'mount',
    'mall',
    'pass',
    'ladder',
    'help',
  ];
  const panel = document.getElementById('panel');
  for (let i = 0; i < tabs.length; i++) {
    h.panels.open(tabs[i]!);
    void panel?.offsetHeight;
    h.panels.close();
    report((i + 1) / (tabs.length + 1));
    await nextFrame();
  }
  if (!h.mapView.isOpen) {
    h.mapView.open();
    h.mapView.close();
  }
  report(1);
}

/** The game's own frames run behind the scene until a stretch of them asks for nothing new. */
async function settle(report: Report): Promise<void> {
  let calm = 0;
  for (let i = 0; calm < CALM_FRAMES && i < 1200; i++) {
    const before = asked();
    await nextFrame();
    if (asked() === before && !waiting()) calm++;
    else {
      calm = 0;
      await drain(() => undefined);
    }
    report(calm / CALM_FRAMES);
  }
}

/** Every frame of the figures made ready is uploaded to the GPU now, not on first sight. */
async function upload(h: WorldHooks, sets: Set<SpriteSet>, report: Report): Promise<void> {
  for (const set of sets) h.renderer.prepareAhead(set);
  // Handing the figures over queues a pass over their cycles in idle time; it is not waited for
  // (see UPLOAD_GRACE_MS): only what is drawn by now is uploaded.
  h.renderer.uploadAhead(0);
  let most = 0;
  let quiet = 0;
  for (;;) {
    const left = h.renderer.uploadAhead(UPLOAD_MS);
    most = Math.max(most, left);
    report(0.2 + 0.8 * (most ? 1 - left / most : 1));
    if (!left && !waiting()) return;
    if (left) {
      quiet = 0;
      await nextFrame();
      continue;
    }
    // Everything drawn is uploaded. What the idle-time pass over the cycles is still drawing is
    // behind the first screens: after a short grace the scene lifts and it finishes in the
    // background (each frame is uploaded as it arrives, or at its first sight).
    quiet ||= performance.now();
    if (performance.now() - quiet > UPLOAD_GRACE_MS) return;
    await nextFrame();
  }
}

/** Everything the first moments of play need, in order (each step waits for the one before). */
export async function warmWorld(h: WorldHooks, load: Pick<Preloader<WorldStep>, 'run'>): Promise<void> {
  const sets = new Set<SpriteSet>();
  const later: (() => void)[] = [];
  // The workers get the machine to themselves while the figures are drawn: the world is drawn
  // behind the scene only from the survey on.
  h.drawWorld(false);
  await load.run('panels', (r) => warmPanels(h, r));
  await load.run('figures', (r) => warmFigures(h, sets, later, r));
  await load.run('scenery', (r) => survey(h, r));
  h.drawWorld(true);
  await load.run('settle', (r) => settle(r));
  h.drawWorld(false);
  await load.run('upload', (r) => upload(h, sets, r));
  h.drawWorld(true);
  // What the first screens do not need, behind the play that has begun (a piece at a time).
  later.forEach((f, i) => setTimeout(() => ahead(f), AFTER_LIFT_MS + i * 150));
  if (import.meta.env.DEV) Object.assign(window, { __handoff: loadingQueues() });
}

/** The work asked for and still queued, by kind (for checking a handoff left nothing behind). */
export const loadingQueues = () => ({
  frames: { ...frameJobs },
  idle: { queued: idleFrames.queued, waiting: idleFrames.waiting() },
  ground: { asked: groundJobs.asked, waiting: groundJobs.waiting() },
});
if (import.meta.env.DEV) Object.assign(window, { __q: loadingQueues });
